import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, MessageCircle, Send, Gavel, ShoppingCart, Clock } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { formatDistanceToNow, differenceInSeconds } from "date-fns";
import { es } from "date-fns/locale";
import { handleImgError } from "../lib/imageFallback";

export const AuctionTimer = ({ endTime, startTime, showHighlight = false }) => {
  const [timeLeft, setTimeLeft] = useState("");
  const [percentRemaining, setPercentRemaining] = useState(100);
  const [status, setStatus] = useState("green"); // green, yellow, red

  useEffect(() => {
    const updateTimer = () => {
      const end = new Date(endTime);
      const now = new Date();
      const diff = differenceInSeconds(end, now);

      if (diff <= 0) {
        setTimeLeft("Finalizado");
        setStatus("red");
        setPercentRemaining(0);
        return;
      }

      // Calculate percentage remaining
      let totalDuration;
      if (startTime) {
        const start = new Date(startTime);
        totalDuration = differenceInSeconds(end, start);
      } else {
        // Default: assume 7 days auction if no start time
        totalDuration = 7 * 24 * 60 * 60;
      }
      
      const percent = Math.min(100, Math.max(0, (diff / totalDuration) * 100));
      setPercentRemaining(percent);

      // Set status based on percentage
      if (percent > 50) {
        setStatus("green");
      } else if (percent > 20) {
        setStatus("yellow");
      } else {
        setStatus("red");
      }

      const days = Math.floor(diff / 86400);
      const hours = Math.floor((diff % 86400) / 3600);
      const minutes = Math.floor((diff % 3600) / 60);
      const seconds = diff % 60;

      // Format based on time remaining
      if (days > 0) {
        setTimeLeft(`${days}d ${hours}h ${minutes}m ${seconds}s`);
      } else if (hours > 0) {
        setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
      } else if (minutes > 0) {
        setTimeLeft(`${minutes}m ${seconds}s`);
      } else {
        setTimeLeft(`${seconds}s`);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [endTime, startTime]);

  const statusColors = {
    green: { bg: "bg-[#10B981]/15", text: "text-[#059669]", border: "border-[#10B981]/30" },
    yellow: { bg: "bg-[#F59E0B]/15", text: "text-[#D97706]", border: "border-[#F59E0B]/30" },
    red: { bg: "bg-[#EF4444]/15", text: "text-[#DC2626]", border: "border-[#EF4444]/30" }
  };

  const colors = statusColors[status];

  if (showHighlight) {
    return (
      <span 
        className={`mono text-xs font-semibold px-2 py-1 rounded-full ${colors.bg} ${colors.text} border ${colors.border}`} 
        data-testid="auction-timer"
      >
        {timeLeft}
      </span>
    );
  }

  return (
    <span className={`mono text-sm font-semibold ${colors.text}`} data-testid="auction-timer">
      {timeLeft}
    </span>
  );
};

export const ScheduledTimer = ({ publishDate }) => {
  const [timeLeft, setTimeLeft] = useState("");
  const [isPublished, setIsPublished] = useState(false);

  useEffect(() => {
    const updateTimer = () => {
      const publish = new Date(publishDate);
      const now = new Date();
      const diff = differenceInSeconds(publish, now);

      if (diff <= 0) {
        setTimeLeft("¡Ya disponible!");
        setIsPublished(true);
        return;
      }

      const days = Math.floor(diff / 86400);
      const hours = Math.floor((diff % 86400) / 3600);
      const minutes = Math.floor((diff % 3600) / 60);
      const seconds = diff % 60;

      if (days > 0) {
        setTimeLeft(`${days}d ${hours}h ${minutes}m`);
      } else if (hours > 0) {
        setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
      } else if (minutes > 0) {
        setTimeLeft(`${minutes}m ${seconds}s`);
      } else {
        setTimeLeft(`${seconds}s`);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [publishDate]);

  return (
    <div className="flex flex-col items-center" data-testid="scheduled-timer">
      <span className="text-xs text-[#F59E0B] font-semibold uppercase">Próximamente</span>
      <span className="mono text-sm font-semibold text-[#64748B]">{timeLeft}</span>
    </div>
  );
};

export const ProductCard = ({ product, onLikeToggle, compact = false, auctionStatus = null, horizontal = false, hideBadge = false, noGrayscale = false }) => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(product.likes_count || 0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Check if user is winning or losing this auction
  const userAuctionStatus = auctionStatus?.find(s => s.product_id === product.product_id);
  const isWinning = userAuctionStatus?.is_winning;
  const isParticipating = !!userAuctionStatus;

  useEffect(() => {
    if (user && token) {
      checkLiked();
    }
  }, [user, token, product.product_id]);

  const checkLiked = async () => {
    try {
      const response = await axios.get(`${API}/products/${product.product_id}/liked`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setLiked(response.data.liked);
    } catch (error) {
      // Not logged in or error
    }
  };

  const handleLike = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      toast.error("Inicia sesión para dar like");
      navigate("/login");
      return;
    }

    try {
      setIsAnimating(true);
      const response = await axios.post(
        `${API}/products/${product.product_id}/like`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      setLiked(response.data.liked);
      setLikesCount(prev => response.data.liked ? prev + 1 : prev - 1);
      if (onLikeToggle) onLikeToggle(product.product_id, response.data.liked);
      setTimeout(() => setIsAnimating(false), 400);
    } catch (error) {
      toast.error("Error al dar like");
      setIsAnimating(false);
    }
  };

  const handleComment = (e) => {
    e.preventDefault();
    navigate(`/product/${product.product_id}#comments`);
  };

  const handleShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const url = `${window.location.origin}/product/${product.product_id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: product.title,
          text: product.description,
          url: url
        });
      } catch (error) {
        // User cancelled
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Enlace copiado al portapapeles");
    }
  };

  const isAuction = product.sale_type === "auction";
  const currentPrice = isAuction ? product.current_bid : product.price;
  const isScheduled = product.is_scheduled && product.scheduled_publish_date;
  const isSold = product.is_sold || product.status === "sold";

  // Horizontal version for horizontal scroll sections
  if (horizontal) {
    let badgeColor = "bg-emerald-500"; // direct sale
    let badgeText = "VENTA";
    
    if (isSold) {
      badgeColor = "bg-gray-500";
      badgeText = "VENDIDO";
    } else if (isScheduled) {
      badgeColor = "bg-amber-500";
      badgeText = "PRÓXIMO";
    } else if (isAuction) {
      badgeColor = "bg-blue-600";
      badgeText = "SUBASTA";
    }

    // Determine if we should apply grayscale (only for sold items and if noGrayscale is false)
    const applyGrayscale = isSold && !noGrayscale;

    return (
      <article 
        className={`product-card bg-white rounded-xl overflow-hidden border border-[#E2E8F0] shadow-sm`} 
        data-testid={`product-card-h-${product.product_id}`}
      >
        <Link to={`/product/${product.product_id}`}>
          <div className="relative">
            <div className={`aspect-square bg-[#F1F5F9] flex items-center justify-center p-2 ${applyGrayscale ? 'grayscale' : ''}`}>
              <img
                src={product.image_url}
                alt={product.title}
                loading="lazy"
                onError={handleImgError}
                className="w-full h-full object-cover rounded-lg"
              />
            </div>
            {/* Badge - only show if hideBadge is false */}
            {!hideBadge && (
              <div className={`absolute top-1.5 left-1.5 ${badgeColor} text-white text-[9px] px-2 py-0.5 rounded-full font-medium`}>
                {badgeText}
              </div>
            )}
            {/* Timer for auctions */}
            {isAuction && !isSold && !isScheduled && product.end_time && (
              <div className="absolute bottom-1.5 right-1.5 bg-black/80 text-white text-[10px] px-2 py-1 rounded-full">
                <AuctionTimer endTime={product.end_time} startTime={product.start_time} />
              </div>
            )}
            {/* Scheduled timer - more visible */}
            {isScheduled && product.scheduled_publish_date && (
              <div className="absolute bottom-2 left-2 right-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs px-3 py-2 rounded-lg shadow-lg flex items-center justify-center gap-2">
                <Clock size={14} className="animate-pulse" />
                <ScheduledTimer publishDate={product.scheduled_publish_date} />
              </div>
            )}
            {/* Sold overlay - only if not noGrayscale */}
            {isSold && !noGrayscale && (
              <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                <span className="bg-white/90 text-gray-700 text-xs font-bold px-3 py-1 rounded-full">FINALIZADO</span>
              </div>
            )}
            {/* Winning/Losing indicator */}
            {!isSold && !isScheduled && isAuction && isParticipating && (
              <div className={`absolute top-1.5 right-1.5 text-[9px] font-bold px-2 py-0.5 rounded-full ${isWinning ? 'bg-green-500 text-white' : 'bg-red-500 text-white animate-pulse'}`}>
                {isWinning ? '✓ GANANDO' : '✗ SUPERADO'}
              </div>
            )}
          </div>
          <div className="p-2.5">
            <h3 className="font-semibold text-xs text-[#0F172A] line-clamp-1">{product.title}</h3>
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-sm font-bold text-[#F97316]">
                ${(currentPrice || product.price || 0)?.toLocaleString()}
              </span>
              {isAuction && !isSold && (
                <span className="text-[10px] text-[#64748B]">
                  {product.bid_count || 0} ofertas
                </span>
              )}
              {!isAuction && !isSold && (
                <span className="text-[10px] text-emerald-600 font-medium">
                  {(product.request_count || product.purchase_intent_count || 0)} Solicitudes
                </span>
              )}
              {isSold && (
                <span className="text-[10px] text-gray-400 font-medium">
                  Vendido
                </span>
              )}
            </div>
          </div>
        </Link>
      </article>
    );
  }

  // Compact version for grid view
  if (compact) {
    // Determine border class based on auction status
    let borderClass = "border-[#E2E8F0]";
    if (isSold) {
      borderClass = "border-[#3B82F6] border-2";
    } else if (isScheduled) {
      borderClass = "border-[#F59E0B] border-2 shadow-[0_0_10px_rgba(245,158,11,0.2)]";
    } else if (isAuction && isParticipating) {
      borderClass = isWinning ? "border-[#10B981] border-2 shadow-[0_0_10px_rgba(16,185,129,0.3)]" : "border-[#EF4444] border-2 shadow-[0_0_10px_rgba(239,68,68,0.3)]";
    }
    
    return (
      <article className={`product-card bg-white rounded-xl overflow-hidden border ${borderClass} feed-item relative`} data-testid={`product-card-${product.product_id}`}>
        {/* Sold indicator */}
        {isSold && (
          <div className="absolute top-0 left-0 right-0 text-center text-xs font-bold py-1 z-10 bg-[#3B82F6] text-white">
            Vendido
          </div>
        )}
        {/* Scheduled indicator */}
        {!isSold && isScheduled && (
          <div className="absolute top-0 left-0 right-0 text-center text-xs font-bold py-1 z-10 bg-[#F59E0B] text-white">
            Próximamente
          </div>
        )}
        {/* Winning/Losing indicator */}
        {!isSold && !isScheduled && isAuction && isParticipating && (
          <div className={`absolute top-0 left-0 right-0 text-center text-xs font-bold py-1 z-10 ${isWinning ? "bg-[#10B981] text-white" : "bg-[#EF4444] text-white"}`}>
            {isWinning ? "✓ Ganando" : "✗ Superado"}
          </div>
        )}
        {/* Image */}
        <Link to={`/product/${product.product_id}`}>
          <div className={`relative aspect-square overflow-hidden bg-[#F1F5F9] ${isSold || isScheduled || isParticipating ? "mt-6" : ""}`}>
            <img
              src={product.image_url}
              alt={product.title}
              loading="lazy"
              onError={handleImgError}
              className={`w-full h-full object-cover transition-transform duration-300 hover:scale-105 ${isScheduled ? "opacity-80 grayscale-[30%]" : ""} ${isSold ? "opacity-70" : ""}`}
            />
            {isSold ? (
              <div className="absolute top-2 right-2 bg-[#3B82F6]/90 backdrop-blur text-white px-2 py-1 rounded-full text-xs font-semibold">
                Finalizado
              </div>
            ) : isScheduled ? (
              <div className="absolute top-2 right-2 bg-[#F59E0B]/90 backdrop-blur text-white px-2 py-1 rounded-full text-xs font-mono">
                <ScheduledTimer publishDate={product.scheduled_publish_date} />
              </div>
            ) : isAuction && product.auction_end_time && (
              <div className="absolute top-2 right-2 bg-[#0F172A]/85 backdrop-blur text-white px-2 py-1 rounded-full text-xs font-mono">
                <AuctionTimer endTime={product.auction_end_time} />
              </div>
            )}
          </div>
        </Link>
        
        {/* Product Type Badge */}
        <div className="bg-[#0F172A] text-white px-3 py-1.5 text-xs font-medium flex items-center gap-1">
          {isAuction ? (
            <><Gavel size={12} /> Subasta</>
          ) : (
            <><ShoppingCart size={12} /> Comprar Ahora / Mejor Oferta</>
          )}
        </div>

        {/* Info */}
        <div className="p-3">
          <Link to={`/product/${product.product_id}`}>
            <h3 className="font-semibold text-sm text-[#0F172A] line-clamp-1 hover:underline">{product.title}</h3>
          </Link>
          
          {/* Seller info with flag and rating */}
          <div className="flex items-center gap-1 mt-1">
            <span className="text-xs text-[#64748B]">{product.user_name} 🇵🇦</span>
            {product.seller_rating > 0 ? (
              <span className="text-xs text-[#F59E0B] flex items-center">
                | {product.seller_rating.toFixed(1)} ★
              </span>
            ) : (
              <span className="text-xs text-[#94A3B8] flex items-center">
                | ★ Nuevo
              </span>
            )}
          </div>
          
          <div className="flex items-center justify-between mt-2">
            <p className="price-tag text-lg font-bold">${(product.final_price || currentPrice)?.toLocaleString()}</p>
            <div className="flex items-center gap-2">
              <button
                onClick={handleLike}
                className={`p-1 ${liked ? "text-[#EF4444]" : "text-[#64748B]"}`}
              >
                <Heart size={18} fill={liked ? "currentColor" : "none"} />
              </button>
              <span className="text-xs text-[#64748B]">{likesCount}</span>
            </div>
          </div>
        </div>
      </article>
    );
  }

  // Full version
  return (
    <article className="product-card bg-white rounded-2xl overflow-hidden border border-[#E2E8F0] feed-item" data-testid={`product-card-${product.product_id}`}>
      {/* Header */}
      <div className="flex items-center gap-3 p-3">
        <Link to={`/profile/${product.user_id}`}>
          <Avatar className="h-9 w-9">
            <AvatarImage src={product.user_picture} alt={product.user_name} />
            <AvatarFallback className="bg-[#475569] text-white text-sm">
              {product.user_name?.charAt(0)?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
        </Link>
        <div className="flex-1 min-w-0">
          <Link to={`/profile/${product.user_id}`} className="font-semibold text-sm text-[#0F172A] hover:underline">
            {product.user_name}
          </Link>
          <p className="text-xs text-[#64748B]">
            {formatDistanceToNow(new Date(product.created_at), { addSuffix: true, locale: es })}
          </p>
        </div>
        {isAuction ? (
          <span className="badge-auction px-3 py-1 rounded-full text-xs flex items-center gap-1" data-testid="badge-auction">
            <Gavel size={12} />
            Subasta
          </span>
        ) : (
          <span className="badge-direct px-3 py-1 rounded-full text-xs flex items-center gap-1" data-testid="badge-direct">
            <ShoppingCart size={12} />
            Venta
          </span>
        )}
      </div>

      {/* Image */}
      <Link to={`/product/${product.product_id}`}>
        <div className="product-image-container">
          <img
            src={product.image_url}
            alt={product.title}
            loading="lazy"
            onError={handleImgError}
            className="transition-transform duration-300 hover:scale-105"
          />
          {isAuction && product.auction_end_time && (
            <div className="auction-overlay" data-testid="auction-overlay">
              <AuctionTimer endTime={product.auction_end_time} />
            </div>
          )}
        </div>
      </Link>

      {/* Actions */}
      <div className="px-3">
        <div className="action-bar">
          <button
            onClick={handleLike}
            className={`action-button ${liked ? "liked" : ""} ${isAnimating ? "heart-beat" : ""}`}
            data-testid="like-button"
          >
            <Heart size={26} fill={liked ? "currentColor" : "none"} />
          </button>
          <button onClick={handleComment} className="action-button" data-testid="comment-button">
            <MessageCircle size={26} />
          </button>
          <button onClick={handleShare} className="action-button" data-testid="share-button">
            <Send size={26} />
          </button>
          
          <div className="ml-auto">
            <Link to={`/product/${product.product_id}`}>
              <Button
                className="rounded-full bg-[#475569] hover:bg-[#334155] text-sm px-5"
                data-testid="view-product-btn"
              >
                {isAuction ? "Ofertar" : "Comprar"}
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="px-3 pb-4">
        <p className="text-sm font-semibold text-[#0F172A] mb-1">
          {likesCount.toLocaleString()} {likesCount === 1 ? "like" : "likes"}
        </p>
        <Link to={`/product/${product.product_id}`}>
          <h3 className="font-semibold text-[#0F172A] mb-1 hover:underline">{product.title}</h3>
        </Link>
        <p className="text-sm text-[#64748B] line-clamp-2 mb-2">{product.description}</p>
        
        <div className="flex items-center justify-between">
          <div>
            <span className="text-xs text-[#64748B]">
              {isAuction ? "Oferta actual" : "Precio"}
            </span>
            <p className="price-tag text-xl">${currentPrice?.toLocaleString()}</p>
          </div>
          {product.comments_count > 0 && (
            <Link to={`/product/${product.product_id}#comments`} className="text-sm text-[#64748B] hover:text-[#0F172A]">
              Ver {product.comments_count} comentario{product.comments_count !== 1 ? "s" : ""}
            </Link>
          )}
        </div>
      </div>
    </article>
  );
};

export default ProductCard;
